/**
 * Error handling utilities designed to work with our fetch wrapper (internalFetch.js).
 * 
 * This handles all error types that can occur during API calls:
 * 1. Regular Error objects from network/runtime failures
 * 2. API error responses (objects with status and additional data)
 * 3. Plain string error messages
 * 4. Any other objects (JSON stringified with context)
 * 
 * Each error is standardized and captured in Sentry for monitoring.
 */

import * as Sentry from "@sentry/react";

type ErrorInput = Error | Record<string, unknown> | string;

interface HttpErrorResponse {
  readonly headers: Headers;
  readonly ok: boolean;
  readonly redirected: boolean;
  readonly status: number;
  readonly statusText: string;
  readonly type: ResponseType;
  readonly url: string;
  [key: string]: unknown;
}

// Type guard for HTTP error responses
const isHttpErrorResponse = (error: unknown): error is HttpErrorResponse => (
  typeof error === 'object' &&
  error !== null &&
  typeof (error as HttpErrorResponse).status === 'number' &&
  typeof (error as HttpErrorResponse).statusText === 'string'
);

/**
 * Creates a standardized error object and captures it in Sentry
 * @param error - The original error, which can be:
 *   - Error: A JavaScript Error object
 *   - ErrorResponse: An HTTP error response with status, statusText, and potentially other fields
 *   - Record<string, unknown>: A plain object (like API error responses)
 *   - string: A simple error message
 * @param context - Description of where/why the error occurred
 * @param tags - Additional tags to add to the error
 * @returns The standardized error object
 */
export function captureError(error: ErrorInput, context: string, tags?: {
  [key: string]: string | number | boolean;
}): Error {
    if (tags) {
      Sentry.setTags(tags);
    }

    let standardizedError: Error;

    if (error instanceof Error) {
      // Modify the original error (maintaining backward compatibility)
      error.message = `${context}: ${error.message}`;
      standardizedError = error;
    } else if (isHttpErrorResponse(error)) {
      standardizedError = new Error(`${context}: ${error.url} HTTP ${error.status} ${error.statusText}`);
    } else {
      let errorMessage: string;
      try {
        errorMessage = JSON.stringify(error);
      } catch (stringifyError) {
        // Fallback if JSON.stringify fails (e.g., circular reference)
        errorMessage = String(error);
      }
      standardizedError = new Error(`${context}: ${errorMessage}`);
    }

    // For backward compatibility, don't add extra context to the captureException call
    Sentry.captureException(standardizedError);
    return standardizedError;
}
